import type { DocNode } from '@atlaskit/adf-schema';

import type { IssueType } from '../providers/BulkCreateContextProvider.tsx';

export type FetchProjectDataProps = {
	/**
	 * Jira Cloud ID for the site we want to get available projects for
	 */
	cloudId?: string;
	/**
	 * Filter the results using a literal string. Projects with a matching key or name are returned (case insensitive).
	 */
	query?: string;
	/**
	 * Jira Project ID for the site we want to get available projects for
	 * Example: ['10000', '10001', '10002']
	 */
	projectIds?: string[];
	/**
	 * Order the results by a field.
	 * Example: 'name' (ascending) or '-name' (descending)
	 * See https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-projects/#api-rest-api-3-project-search-get-request-Query%20parameters for more details.
	 */
	orderBy?: string;
	/**
	 * AbortSignal to cancel the request
	 */
	signal?: AbortSignal;
	onComplete?: (
		projects: JiraProjects | undefined,
		recentProjects: JiraProjects | undefined,
	) => void;
};

export type FetchIssueTypeDataProps = {
	/**
	 * Jira Cloud ID for the site we want to get available projects for
	 */
	cloudId?: string;
	/**
	 * Jira Project ID for the site we want to get available issue types for
	 */
	projectId?: string;
	/**
	 * AbortSignal to cancel the request
	 */
	signal?: AbortSignal;
	onComplete?: (issueTypes: JiraIssueTypes | undefined) => void;
};

export type CreateBulkIssueProps = {
	limit: number;
	cloudId: string;
	projectId: string;
};

export type CreateIssueProps = {
	cloudId: string;
	issueBody: CreateIssueBody;
};

export type CreateBulkIssuesBody = {
	issueUpdates: CreateIssueBody[];
};

export type CreateBulkIssuesProps = {
	cloudId: string;
	issueBody: CreateBulkIssuesBody;
};

export type UseCreateIssueProps = {
	cloudId: string;
	issueId: string;
};

// Encapsulates any type that is an object with an id field.
// GIC uses the ID field to correctly place the value in the given field.
export type GICCompatibleObject = {
	id: string;
}

export type CreateIssueBodyIssueLinkType = GICCompatibleObject & {
	key: string;
};

export type CreateIssueBodyPriorityType = GICCompatibleObject & {
	name: string;
};

export type CreateIssueBodySelectOptionType = {
	id: string;
	value: string;
};

export const isGICCompatibleObject = (value: unknown): value is GICCompatibleObject => {
	return typeof value === 'object' && value !== null && 'id' in value;
};

export const isGICCompatibleObjectList = (value: unknown): value is GICCompatibleObject[] => {
	return Array.isArray(value) && value.every(isGICCompatibleObject);
};

// Expand on this to support more value types
export type SupportedValueType =
	| string
	| string[]
	| number
	| DocNode
	| CreateIssueBodyIssueLinkType
	| CreateIssueBodyPriorityType
	| GICCompatibleObject
	| GICCompatibleObject[]
	| CreateIssueBodySelectOptionType[]
	| undefined;
export type CreateIssueBodyFields = {
	description?: DocNode;
	issuetype: GICCompatibleObject;
	project: GICCompatibleObject;
	parent?: CreateIssueBodyIssueLinkType;
	assignee?: GICCompatibleObject;
	labels?: string[];
	summary: string;
	[key: string]: SupportedValueType;
};

export type CreateIssueBody = {
	fields: CreateIssueBodyFields;
};

type CreateIssueResponseTransitionErrorCollection = {
	errorMessages: [];
	errors: {};
};

type CreateIssueResponseTransition = {
	status: number;
	errorCollection: CreateIssueResponseTransitionErrorCollection;
};

export type CreateIssueResponse = {
	id: string;
	key: string;
	self: string;
	transition: CreateIssueResponseTransition;
	summary: string;
	rowIndex?: number;
};

export type CreateAllIssueResponse = {
	createdIssueIDs: string[];
	createdIssues: CreateIssueResponse[];
	failedIssueIDs: string[];
};

type CreateBulkIssuesElementError = {
	errorMessages: string[];
	errors: any;
	status?: number;
};

type CreateBulkIssuesError = {
	elementErrors: CreateBulkIssuesElementError;
	failedElementNumber: number;
	status: number;
};

export type CreateBulkIssuesResponse = {
	errors: CreateBulkIssuesError[];
	issues: CreateIssueResponse[];
};

type AvatarUrls = {
	'16x16': string;
	'24x24': string;
	'32x32': string;
	'48x48': string;
};

export enum JiraProjectType {
	JiraCore = 'jira-core',
	JiraServiceDesk = 'jira-servicedesk',
	JiraCustomerService = 'jira-customer-service',
	JiraSoftware = 'jira-software',
	JiraProductDiscovery = 'jira-product-discovery',
}
type MessageProjectTypeToApiProjectType = {
	[key: string]: JiraProjectType;
};

export const MESSAGE_PROJECT_TYPE_TO_API_PROJECT_TYPE: MessageProjectTypeToApiProjectType = {
	service_desk: JiraProjectType.JiraServiceDesk,
	software: JiraProjectType.JiraSoftware,
	customer_service: JiraProjectType.JiraCustomerService,
	business: JiraProjectType.JiraCore,
	product_discovery: JiraProjectType.JiraProductDiscovery,
};

export type JiraProject = {
	entityId: string;
	expand: string;
	isPrivate: boolean;
	projectTypeKey: string;
	self: string;
	simplified: boolean;
	style: string;
	uuid: string;
	name: string;
	key: string;
	id: string;
	avatarUrls: AvatarUrls;
};

export type JiraProjects = JiraProject[];

export type JiraPriority = {
	description: string;
	iconUrl: string;
	id: string;
	isDefault: boolean;
	name: string;
	statusColor: string;
};

export type JiraPriorities = JiraPriority[];

export type JiraIssue = {
	description: string;
	iconUrl: string;
	id: string;
	name: string;
	self: string;
	subtask: boolean;
	key: string;
};

export type SimpleJiraIssue = {
	issueId: string;
	key: string;
};

export type JiraIssueTypes = IssueType[];

export interface FieldMetadata {
	fieldId: string;
	hasDefaultValue: boolean;
	key: string;
	name: string;
	required: boolean;
	schema: FieldMetadataSchema;
	allowedValues?: JiraPriorities | unknown[];
	operations?: FieldMetadataSchemaOperations[];
}
export interface FieldMetadataSchema {
	type: FieldMetadataSchemaType;
	custom?: string;
	system?: string;
	items?: FieldMetadataSchemaItems;
}

export enum FieldMetadataSchemaItems {
	VERSION = 'version',
	OPTION = 'option',
}

export enum FieldMetadataSchemaType {
	ANY = 'any',
	STRING = 'string',
	NUMBER = 'number',
	DATE = 'date',
	DATETIME = 'datetime',
	ISSUELINK = 'issuelink',
	ISSUETYPE = 'issuetype',
	USER = 'user',
	TEAM = 'team',
	PRIORITY = 'priority',
	ARRAY = 'array',
	COMPONENTS = 'components',
}

export enum FieldMetadataSchemaCustom {
	TEXT_AREA = 'com.atlassian.jira.plugin.system.customfieldtypes:textarea',
	TEXT_FIELD = 'com.atlassian.jira.plugin.system.customfieldtypes:textfield',
	PEOPLE = 'com.atlassian.jira.plugin.system.customfieldtypes:people',
}

export enum FieldMetadataSchemaSystem {
	LABELS = 'labels',
	PARENT = 'parent',
}

export enum FieldMetadataSchemaOperations {
	ADD = 'add',
	SET = 'set',
	REMOVE = 'remove',
}

export interface JiraIssueMetadataResponse {
	fields: FieldMetadata[];
	maxResults: number;
	startAt: number;
	total: number;
}

export interface JiraAiEnabledResponse {
	isEnabled: boolean;
}

// Used to guarantee uniqueness of Jira types.
export class CustomSet<T> {
	private internalMap: Map<string, T>;
	private customIdFn: (item: T) => string;

	constructor(priorities: T[] = [], customIdFn: (item: T) => string) {
		this.internalMap = new Map(priorities.map((priority) => [customIdFn(priority), priority]));
		this.customIdFn = customIdFn;
	}

	public add(priority: T): void {
		// It's assumed that no two priorities will have the same ID so we don't have to worry about overwriting.
		this.internalMap.set(this.customIdFn(priority), priority);
	}

	public has(priority: T): boolean {
		return this.internalMap.has(this.customIdFn(priority));
	}

	public get(key: string): T | undefined {
		return this.internalMap.get(key);
	}

	public hasId(id: string): boolean {
		return this.internalMap.has(id);
	}

	public union(other: CustomSet<T>): CustomSet<T> {
		return new CustomSet<T>([...other.values(), ...this.values()], this.customIdFn);
	}

	public values(): T[] {
		return Array.from(this.internalMap.values());
	}

	public size(): number {
		return this.internalMap.size;
	}
}

export class JiraPrioritiesSet extends CustomSet<JiraPriority> {
	constructor(priorities: JiraPriority[] = []) {
		super(priorities, (priority) => priority.id);
	}
}
export class SimpleJiraIssuesSet extends CustomSet<SimpleJiraIssue> {
	constructor(issues: SimpleJiraIssue[] = []) {
		super(issues, (issue) => issue.key);
	}
}

export type IssueTypeAndId = {
	issueType: IssueType;
	// The ID of the issue, not the type
	id: string;
};

export type JiraIssueKeysToTypes = Map<string, IssueTypeAndId>;

export type JiraComponent = {
	id: string;
	name: string;
	description?: string;
	lead: {
		accountId: string;
		accountType: string;
		self: string;
		avatarUrls: AvatarUrls;
		displayName: string;
		active: boolean;
	};
	self: string;
	projectId: string;
	projectName: string;
	projectKey: string;
};

export type JiraComponents = JiraComponent[];

export type JiraVersion = {
	id: string;
	name: string;
	description?: string;
	archived: boolean;
	released: boolean;
	releaseDate?: string;
	overdue?: boolean;
	userReleaseDate?: string;
	projectId: string;
	self: string;
};

export type JiraVersions = JiraVersion[];

export function isIdNameObject(value: unknown): value is {id: string, name: string} {
	return typeof value === 'object' && value !== null && 'id' in value && 'name' in value;
}

export function isIdNameObjectList(value: unknown): value is {id: string, name: string}[] {
	return Array.isArray(value) && value.every(isIdNameObject);
}

export type DropdownOption = {
	id: string;
	value: string;
};

export type DropdownOptions = DropdownOption[];

export function isSelectOption(value: unknown): value is DropdownOption {
	return typeof value === 'object' && value !== null && 'id' in value && 'value' in value;
}

export function isSelectOptionList(value: unknown): value is DropdownOptions {
	return Array.isArray(value) && value.every(isSelectOption);
}

export type TransitionResponseItem = {
	id: string;
	name: string;
	isConditional: boolean;
	hasValidators: boolean;
	hasScreen: boolean;
	isGlobal: boolean;
	isInitial: boolean;
	isDisabled: boolean;
	to: {
		id: string;
		name: string;
		translatedName?: string;
		statusCategory: {
			id: number;
			name: string;
		};
	};
	validations: Array<{
		unspecified: boolean;
		fieldValidations: Array<{
			fields?: string[];
			type: 'FIELDREQUIREDVALIDATOR';
			errorMessage?: string;
		}>;
	}>;
};

export type TransitionResponseItems = TransitionResponseItem[];

/**
 * GraphQL client utility function that uses the correct endpoint and formatting
 */
export interface GraphQLQuery {
	query: string;
	variables?: Record<string, any>;
	operationName: string;
}

export interface GraphQLResponse<T = any> {
	data?: T;
	errors?: Array<{
		message: string;
		locations?: Array<{
			line: number;
			column: number;
		}>;
		path?: Array<string | number>;
		extensions?: Record<string, any>;
	}>;
}
